import React from 'react';

import type {ITimeSlot} from '../../types/ITimeSlot';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {BodyMediumBold} from '@spaced-out/ui-design-system/lib/components/Text';

import {
  selectCurrentDateSlots,
  selectSelectedTimeSlot,
} from '../../selectors/booking-selector';
import {selectTimezone} from '../../selectors/timezoneSelectors';
import {formatAvailabilityTime} from '../../utils/booking';
import {useRootMachine} from '../../hooks/useRootMachine';
import {useBookingMachine} from '../../hooks/useBookingMachine';

import {classify} from '@spaced-out/ui-design-system/lib/utils/classify';
import {Row, Col} from '@spaced-out/ui-design-system/lib/components/Grid';

import css from './booking-timeslot-picker.css';


export const BookingTimeslotPicker = () => {
  const {language, t, makeKey} = useLabelTranslation();

  const {rootActor, rootState} = useRootMachine();
  const {bookingActor} = useBookingMachine();
  const isRescheduleFlow = rootState.matches('rescheduleFlow');
  const availableTimeSlots = useSelector(bookingActor, selectCurrentDateSlots);
  const selectedTimeSlot = useSelector(bookingActor, selectSelectedTimeSlot);
  const timezone = useSelector(rootActor, selectTimezone);

  const handleTimeSlotSelect = (timeSlot: ITimeSlot) => {
    bookingActor.send({type: 'timeslot.submit', timeSlot});
  };

  return (
    <div
      className={classify(css.bookingTimeslotPickerContainer, {
        [css.bookingFlow]: !isRescheduleFlow,
      })}
    >
      <div className={css.bookingTimeslotPickerHeader}>
        <BodyMediumBold>
          {t('Select_a_time_slot', 'Select a time slot')}
        </BodyMediumBold>
        <BodyMediumBold>
          {timezone ? t(makeKey(timezone), timezone) : ''}
        </BodyMediumBold>
      </div>
      <Row gridType="autoFill" className={css.timeSlotButtonContainer}>
        {Array.isArray(availableTimeSlots) &&
          availableTimeSlots.map((timeSlot: ITimeSlot) => (
            <Col key={timeSlot.start_time} className={css.timeSlotButtonItem}>
              <Button
                key={timeSlot.start_time}
                size="small"
                type="tertiary"
                classNames={{
                  wrapper: classify(css.timeSlotButton, {
                    [css.timeSlotButtonSelected]:
                      selectedTimeSlot?.start_time === timeSlot.start_time,
                  }),
                }}
                onClick={() => handleTimeSlotSelect(timeSlot)}
              >
                {formatAvailabilityTime(timeSlot.start_time, language)}
              </Button>
            </Col>
          ))}
      </Row>
    </div>
  );
};
